import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  FormControlLabel, 
  Switch, 
  Card, 
  CardContent, 
  CircularProgress, 
  Snackbar, 
  Alert,
  Divider
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../firebase';
import { createClub } from '../../services/clubService';
import TopBar from '../TopBar';

function ClubCreate() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // 폼 상태
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    visibility: 'public',
    settings: {
      showHighlights: true,
      showMemos: true,
      defaultShare: false
    }
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (!user) {
        setSnackbar({
          open: true,
          message: '모임을 만들려면 로그인이 필요합니다.',
          severity: 'warning'
        });
        navigate('/clubs');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setSnackbar({
        open: true,
        message: '모임 이름을 입력해주세요.',
        severity: 'error'
      });
      return;
    }

    if (formData.name.length > 50) {
      setSnackbar({
        open: true,
        message: '모임 이름은 50자 이하여야 합니다.',
        severity: 'error'
      });
      return;
    }

    if (formData.description && formData.description.length > 200) {
      setSnackbar({
        open: true,
        message: '모임 설명은 200자 이하여야 합니다.',
        severity: 'error'
      });
      return;
    }

    try {
      setLoading(true);
      const clubId = await createClub(formData);
      
      setSnackbar({
        open: true,
        message: '모임이 성공적으로 생성되었습니다!',
        severity: 'success'
      });
      
      // 잠시 후 모임 상세 페이지로 이동
      setTimeout(() => {
        navigate(`/clubs/${clubId}`);
      }, 1500);
      
    } catch (error) {
      console.error('Error creating club:', error);
      setSnackbar({
        open: true,
        message: '모임 생성 중 오류가 발생했습니다.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const getVisibilityDescription = (visibility) => {
    switch (visibility) {
      case 'public':
        return '누구나 모임을 찾고 가입할 수 있습니다.';
      case 'private':
        return '초대 링크가 있는 사람만 가입할 수 있습니다.';
      case 'secret':
        return '모임이 검색되지 않고, 초대 링크로만 접근 가능합니다.';
      default:
        return '';
    }
  };

  if (!currentUser) {
    return (
      <Box sx={{ pb: 7, maxWidth: '600px', margin: '0 auto', width: '100%' }}>
        <TopBar title="모임 만들기" showBack={true} />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 7, maxWidth: '600px', margin: '0 auto', width: '100%' }}>
      <TopBar title="모임 만들기" showBack={true} />
      
      <Box component="form" onSubmit={handleSubmit} sx={{ p: 2 }}>
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#333' }}>
              기본 정보
            </Typography>
            
            <TextField
              fullWidth
              label="모임 이름 *"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              sx={{ mb: 3 }}
              inputProps={{ maxLength: 50 }}
              helperText={`${formData.name.length}/50자`}
            />
            
            <TextField
              fullWidth
              label="모임 설명"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              multiline
              rows={3}
              sx={{ mb: 3 }}
              inputProps={{ maxLength: 200 }}
              helperText={`${formData.description.length}/200자`}
              placeholder="모임에 대한 간단한 설명을 작성해주세요."
            />
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>공개 범위</InputLabel>
              <Select
                value={formData.visibility}
                label="공개 범위"
                onChange={(e) => handleInputChange('visibility', e.target.value)}
              >
                <MenuItem value="public">공개</MenuItem>
                <MenuItem value="private">비공개</MenuItem>
                <MenuItem value="secret">비밀</MenuItem>
              </Select>
            </FormControl>
            
            <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
              {getVisibilityDescription(formData.visibility)}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#333' }}>
              표시 설정
            </Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.settings.showHighlights}
                  onChange={(e) => handleInputChange('settings.showHighlights', e.target.checked)}
                />
              }
              label="문장 표시"
              sx={{ mb: 2 }}
            />
            <Typography variant="body2" sx={{ color: '#666', mb: 3, fontSize: '0.875rem' }}>
              멤버들의 문장을 모임에서 볼 수 있습니다.
            </Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.settings.showMemos}
                  onChange={(e) => handleInputChange('settings.showMemos', e.target.checked)}
                />
              }
              label="메모 표시"
              sx={{ mb: 2 }}
            />
            <Typography variant="body2" sx={{ color: '#666', mb: 3, fontSize: '0.875rem' }}>
              멤버들의 메모를 모임에서 볼 수 있습니다.
            </Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.settings.defaultShare}
                  onChange={(e) => handleInputChange('settings.defaultShare', e.target.checked)}
                />
              }
              label="기본 공유"
              sx={{ mb: 2 }}
            />
            <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
              새로 추가되는 멤버의 기본 공유 설정입니다.
            </Typography>
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => navigate('/clubs')}
            disabled={loading}
            sx={{ py: 1.5 }}
          >
            취소
          </Button>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading || !formData.name.trim()}
            sx={{ 
              py: 1.5,
              bgcolor: '#FFD700',
              color: '#333',
              '&:hover': {
                bgcolor: '#FFE55C',
              },
              '&:disabled': {
                bgcolor: '#f5f5f5',
                color: '#ccc'
              }
            }}
          >
            {loading ? <CircularProgress size={20} /> : '모임 만들기'}
          </Button>
        </Box>
      </Box>

      {/* 스낵바 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ClubCreate;
